import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, User, Mail, Hash, Calendar, ExternalLink, History, Rocket, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { adminCheck, adminGetProject, adminUpdateProject, adminPromoteToCB } from "../lib/api";
import { PREVIEW_STATUS_META, statusLabel } from "../data/previewStatus";

const STATUS_OPTIONS = Object.keys(PREVIEW_STATUS_META);
const PRIORITY_OPTIONS = ["low", "normal", "high", "urgent"];
const PREVIEW_STATUS_OPTIONS = ["not_created", "building", "live", "offline"];

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const loadProject = useCallback(async () => {
    const res = await adminGetProject(id);
    setData(res);
    const p = res.project;
    setForm({
      project_status: p.project_status || "",
      priority: p.priority || "normal",
      progress_percentage: p.progress_percentage ?? 0,
      estimated_delivery_date: p.estimated_delivery_date || "",
      preview_url: p.preview_url || "",
      preview_status: p.preview_status || "not_created",
      internal_notes: p.internal_notes || "",
      customer_visible_update: p.customer_visible_update || "",
      assignee: p.assignee || "",
      revision_limit: p.revision_limit ?? 1,
      revisions_used: p.revisions_used ?? 0,
    });
  }, [id]);

  useEffect(() => {
    adminCheck()
      .then((r) => {
        if (!r.admin) {
          setAuthorized(false);
          navigate("/");
          return;
        }
        setAuthorized(true);
        loadProject().catch(() => navigate("/admin"));
      })
      .catch(() => {
        setAuthorized(false);
        navigate("/");
      });
  }, [navigate, loadProject]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.progress_percentage = Number(payload.progress_percentage);
      payload.revision_limit = Number(payload.revision_limit);
      payload.revisions_used = Number(payload.revisions_used);
      await adminUpdateProject(id, payload);
      toast.success("Project updated");
      await loadProject();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (authorized === null || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]">
        <Loader2 className="animate-spin text-[#D4AF37]" size={30} />
      </div>
    );
  }

  const { project, request, customer, activity } = data;
  const cb = data.continue_building;

  const openContinueBuilding = async () => {
    if (cb?.id) {
      navigate(`/admin/continue-building/${cb.id}`);
      return;
    }
    setPromoting(true);
    try {
      const res = await adminPromoteToCB(id);
      toast.success(res.created ? "Moved into Continue Building" : "Opening Continue Building");
      navigate(`/admin/continue-building/${res.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Continue Building");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-project-detail">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-[#0B0B0B]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
            data-testid="admin-back-btn"
          >
            <ArrowLeft size={16} /> Queue
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-50"
            data-testid="admin-save-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save changes
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-8">
          <div>
            <p className="text-xs text-[#71717A] mb-1">{project.order_number}</p>
            <h1 className="font-display text-3xl font-bold">{project.project_name}</h1>
          </div>

          {/* Workspace controls */}
          <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-5" data-testid="admin-workspace">
            <h2 className="font-display font-semibold text-lg">Project Controls</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Status">
                <Select value={form.project_status} onChange={(v) => set("project_status", v)} options={STATUS_OPTIONS} labeler={statusLabel} testid="field-status" />
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onChange={(v) => set("priority", v)} options={PRIORITY_OPTIONS} testid="field-priority" />
              </Field>
              <Field label="Progress (%)">
                <input
                  type="number" min="0" max="100"
                  value={form.progress_percentage}
                  onChange={(e) => set("progress_percentage", e.target.value)}
                  className="admin-input"
                  data-testid="field-progress"
                />
              </Field>
              <Field label="Estimated delivery">
                <input
                  type="date"
                  value={form.estimated_delivery_date}
                  onChange={(e) => set("estimated_delivery_date", e.target.value)}
                  className="admin-input [color-scheme:dark]"
                  data-testid="field-delivery"
                />
              </Field>
              <Field label="Assignee">
                <input value={form.assignee} onChange={(e) => set("assignee", e.target.value)} className="admin-input" placeholder="Team member" data-testid="field-assignee" />
              </Field>
              <Field label="Preview status">
                <Select value={form.preview_status} onChange={(v) => set("preview_status", v)} options={PREVIEW_STATUS_OPTIONS} testid="field-preview-status" />
              </Field>
              <Field label="Revision limit">
                <input type="number" min="0" value={form.revision_limit} onChange={(e) => set("revision_limit", e.target.value)} className="admin-input" data-testid="field-rev-limit" />
              </Field>
              <Field label="Revisions used">
                <input type="number" min="0" value={form.revisions_used} onChange={(e) => set("revisions_used", e.target.value)} className="admin-input" data-testid="field-rev-used" />
              </Field>
            </div>
            <Field label="Live preview URL">
              <input value={form.preview_url} onChange={(e) => set("preview_url", e.target.value)} className="admin-input" placeholder="https://..." data-testid="field-preview-url" />
            </Field>
          </section>

          {/* Continue Building bridge */}
          <section className="bg-[#D4AF37]/[0.06] border border-[#D4AF37]/30 rounded-2xl p-6" data-testid="admin-cb-bridge">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center shrink-0"><Rocket size={19} /></div>
                <div>
                  <h2 className="font-display font-semibold text-lg">Build & Phases</h2>
                  <p className="text-sm text-[#A1A1AA] mt-0.5 max-w-md">
                    {cb
                      ? `This project is in Continue Building — ${cb.progress_percentage || 0}% complete. Manage phases, releases and deployment there.`
                      : "Move this paid project into Continue Building to manage its build phases, releases and confirm overall progress."}
                  </p>
                </div>
              </div>
              <button
                onClick={openContinueBuilding}
                disabled={promoting}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-50 shrink-0"
                data-testid="admin-open-cb-btn"
              >
                {promoting ? <Loader2 className="animate-spin" size={15} /> : <ArrowRight size={15} />}
                {cb ? "Open in Continue Building" : "Move to Continue Building"}
              </button>
            </div>
          </section>

          {/* Notes */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
              <h3 className="font-medium text-sm mb-2 text-amber-300">Internal notes <span className="text-[#71717A] font-normal">(team only)</span></h3>
              <textarea
                value={form.internal_notes}
                onChange={(e) => set("internal_notes", e.target.value)}
                rows={5}
                className="admin-input resize-none"
                placeholder="Private notes for the team..."
                data-testid="field-internal-notes"
              />
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
              <h3 className="font-medium text-sm mb-2 text-emerald-300">Customer update <span className="text-[#71717A] font-normal">(visible)</span></h3>
              <textarea
                value={form.customer_visible_update}
                onChange={(e) => set("customer_visible_update", e.target.value)}
                rows={5}
                className="admin-input resize-none"
                placeholder="Message shown to the customer..."
                data-testid="field-customer-update"
              />
            </div>
          </section>

          {/* Request details */}
          {request && (
            <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6" data-testid="admin-request-details">
              <h2 className="font-display font-semibold text-lg mb-4">Request Details</h2>
              <dl className="space-y-3 text-sm">
                <Detail label="Category" value={request.app_category} />
                <Detail label="Idea" value={request.idea_description} />
                <Detail label="Problem" value={request.problem_statement} />
                <Detail label="Target users" value={request.target_users} />
                <Detail label="Requested features" value={request.requested_features} />
                <Detail label="Design style" value={request.design_style} />
                <Detail label="Preferred colors" value={request.preferred_colors} />
                <Detail label="Reference links" value={request.reference_links} />
                <Detail label="Notes" value={request.additional_notes} />
              </dl>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
            <h3 className="font-medium text-sm mb-4">Customer</h3>
            <div className="space-y-2 text-sm text-[#A1A1AA]">
              <p className="flex items-center gap-2"><User size={14} /> {customer?.name || "—"}</p>
              <p className="flex items-center gap-2"><Mail size={14} /> {customer?.email || request?.customer_email || "—"}</p>
              <p className="flex items-center gap-2"><Hash size={14} /> {project.order_number}</p>
              <p className="flex items-center gap-2"><Calendar size={14} /> {(project.created_at || "").slice(0, 10)}</p>
              <p className="flex items-center gap-2 text-[#D4AF37]"><span className="text-lg font-semibold">${project.amount}</span></p>
            </div>
            {form.preview_url && (
              <a
                href={form.preview_url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline"
                data-testid="admin-open-preview"
              >
                Open live preview <ExternalLink size={12} />
              </a>
            )}
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
            <h3 className="font-medium text-sm mb-4 flex items-center gap-2"><History size={15} /> Activity</h3>
            <ol className="space-y-4" data-testid="admin-activity">
              {(activity || []).length === 0 && <p className="text-xs text-[#71717A]">No activity yet.</p>}
              {(activity || []).map((a) => (
                <li key={a.id} className="relative pl-4 border-l border-white/10">
                  <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#D4AF37]" />
                  <p className="text-sm">{a.description}</p>
                  <p className="text-xs text-[#71717A] mt-0.5">{(a.created_at || "").replace("T", " ").slice(0, 16)}</p>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs text-[#71717A] mb-1.5">{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options, labeler, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="admin-input capitalize [&>option]:text-black"
    data-testid={testid}
  >
    {options.map((o) => (
      <option key={o} value={o}>{labeler ? labeler(o) : o.replace(/_/g, " ")}</option>
    ))}
  </select>
);

const Detail = ({ label, value }) =>
  value ? (
    <div>
      <dt className="text-[#71717A] text-xs">{label}</dt>
      <dd className="text-white/90 whitespace-pre-wrap">{value}</dd>
    </div>
  ) : null;
